/** Shared SEO metadata — used by client pageMeta (ESM). */
/** Build scripts use scripts/marketing-seo.cjs — keep both in sync. */

export const SITE_NAME = 'Racontera';

export const DEFAULT_META_DESCRIPTION =
  'Discover content opportunities, organize research, create source-backed outlines, and publish with confidence. Racontera helps writers move from research to published content without losing context.';

export const HOME_TITLE = 'Racontera - Research, Write & Publish Better Content';
export const HOME_DESCRIPTION = DEFAULT_META_DESCRIPTION;

export const BLOG_INDEX_DESCRIPTION =
  'Updates on research-driven content, product news, and workflow tips from the Racontera team.';

const DEFAULT_SHARE_IMAGE_PATH = '/branding/racontera-logo-wordmark.png';

/** @param {string} [siteOrigin] */
/** @param {string} [publicUrl] */
export function createUrlResolver(siteOrigin = '', publicUrl = '') {
  const origin = (siteOrigin || '').replace(/\/$/, '');
  const base = (publicUrl || '').replace(/\/$/, '');

  function absoluteUrl(path) {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return `${origin}${base}${normalized}`;
  }

  function defaultShareImage() {
    return absoluteUrl(DEFAULT_SHARE_IMAGE_PATH);
  }

  return { absoluteUrl, defaultShareImage };
}

/** Fallback meta description from markdown body when excerpt is omitted. */
export function descriptionFromBody(body, maxLength = 160) {
  if (!body) return '';
  const plain = body
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/[#*_`[\]()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= maxLength) return plain;
  return `${plain.slice(0, maxLength - 1).trim()}…`;
}

function publisherSchema(resolver) {
  return {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: {
      '@type': 'ImageObject',
      url: resolver.defaultShareImage(),
    },
  };
}

/** @param {{ name: string, type?: 'Person' | 'Organization' } | null | undefined} author */
function authorSchema(resolver, author) {
  if (!author?.name) return publisherSchema(resolver);
  if (author.type === 'Organization') {
    return {
      '@type': 'Organization',
      name: author.name,
      logo: {
        '@type': 'ImageObject',
        url: resolver.defaultShareImage(),
      },
    };
  }
  return {
    '@type': 'Person',
    name: author.name,
  };
}

function resolvePostImage(resolver, post) {
  if (!post?.image) return undefined;
  if (post.image.startsWith('http')) return post.image;
  const path = post.image.startsWith('/') ? post.image : `/${post.image}`;
  return resolver.absoluteUrl(path);
}

/**
 * @param {ReturnType<typeof createUrlResolver>} resolver
 * @param {{
 *   title: string;
 *   description?: string;
 *   path?: string;
 *   ogType?: 'website' | 'article';
 *   image?: string;
 *   headline?: string;
 *   datePublished?: string;
 *   dateModified?: string;
 *   schema?: 'WebPage' | 'Blog' | 'BlogPosting' | null;
 *   blogPosts?: Array<{ title: string; slug: string; date?: string; excerpt?: string; author?: { name: string; type?: string } | null }>;
 *   author?: { name: string; type?: 'Person' | 'Organization' } | null;
 * }} options
 */
export function buildSeoPayload(resolver, options) {
  const {
    title,
    description: metaDescription = '',
    path = '/',
    ogType = 'website',
    image,
    headline,
    datePublished,
    dateModified,
    schema = 'WebPage',
    blogPosts,
    author,
  } = options;

  const canonicalUrl = resolver.absoluteUrl(path);
  const shareImage = image || resolver.defaultShareImage();
  const desc = metaDescription ?? '';

  let jsonLd = null;
  if (schema === 'BlogPosting') {
    jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: headline || title,
      description: desc,
      datePublished: datePublished || undefined,
      dateModified: dateModified || datePublished || undefined,
      author: authorSchema(resolver, author),
      publisher: publisherSchema(resolver),
      image: shareImage,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl,
      },
      url: canonicalUrl,
    };
  } else if (schema === 'Blog' && blogPosts) {
    jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: `${SITE_NAME} Blog`,
      description: desc,
      url: canonicalUrl,
      publisher: publisherSchema(resolver),
      blogPost: blogPosts.map((post) => ({
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt || undefined,
        datePublished: post.date || undefined,
        url: resolver.absoluteUrl(`/blog/${post.slug}`),
        ...(post.author?.name
          ? { author: authorSchema(resolver, post.author) }
          : {}),
      })),
    };
  } else if (schema === 'WebPage') {
    jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description: desc,
      url: canonicalUrl,
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: resolver.absoluteUrl('/'),
      },
    };
  }

  return {
    title,
    description: desc,
    path,
    canonicalUrl,
    shareImage,
    ogType,
    datePublished,
    dateModified: dateModified || datePublished,
    author: author || null,
    jsonLd,
  };
}

export function buildHomeSeo(resolver) {
  return buildSeoPayload(resolver, {
    title: HOME_TITLE,
    description: HOME_DESCRIPTION,
    path: '/',
    ogType: 'website',
    schema: 'WebPage',
  });
}

export function buildBlogIndexSeo(resolver, blogPosts) {
  return buildSeoPayload(resolver, {
    title: 'Blog — Racontera',
    description: BLOG_INDEX_DESCRIPTION,
    path: '/blog',
    ogType: 'website',
    schema: 'Blog',
    blogPosts,
  });
}

export function buildBlogPostSeo(resolver, post) {
  const description = (post.excerpt || '').trim() || descriptionFromBody(post.body);
  const published = post.date ? `${post.date}T00:00:00.000Z` : undefined;
  return buildSeoPayload(resolver, {
    title: `${post.title} — Racontera`,
    description,
    path: `/blog/${post.slug}`,
    ogType: 'article',
    headline: post.title,
    datePublished: published,
    dateModified: published,
    image: resolvePostImage(resolver, post),
    author: post.author || null,
    schema: 'BlogPosting',
  });
}

export function buildBlogPostNotFoundSeo(resolver, slug) {
  return buildSeoPayload(resolver, {
    title: 'Post not found — Racontera',
    description: 'The blog post you requested could not be found.',
    path: `/blog/${slug || ''}`,
    ogType: 'website',
    schema: 'WebPage',
  });
}

function escapeAttr(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}

/** @param {ReturnType<typeof buildSeoPayload>} seo */
export function renderHeadExtras(seo) {
  const lines = [
    `<link rel="canonical" href="${escapeAttr(seo.canonicalUrl)}" />`,
    `<meta property="og:title" content="${escapeAttr(seo.title)}" />`,
    `<meta property="og:description" content="${escapeAttr(seo.description)}" />`,
    `<meta property="og:type" content="${escapeAttr(seo.ogType)}" />`,
    `<meta property="og:url" content="${escapeAttr(seo.canonicalUrl)}" />`,
    `<meta property="og:site_name" content="${escapeAttr(SITE_NAME)}" />`,
    `<meta property="og:image" content="${escapeAttr(seo.shareImage)}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${escapeAttr(seo.title)}" />`,
    `<meta name="twitter:description" content="${escapeAttr(seo.description)}" />`,
    `<meta name="twitter:image" content="${escapeAttr(seo.shareImage)}" />`,
  ];

  if (seo.ogType === 'article' && seo.datePublished) {
    lines.push(
      `<meta property="article:published_time" content="${escapeAttr(seo.datePublished)}" />`,
      `<meta property="article:modified_time" content="${escapeAttr(seo.dateModified)}" />`
    );
    if (seo.author?.name && seo.author.type === 'Person') {
      lines.push(
        `<meta property="article:author" content="${escapeAttr(seo.author.name)}" />`
      );
    }
  }

  if (seo.jsonLd) {
    lines.push(
      `<script type="application/ld+json">${JSON.stringify(seo.jsonLd)}</script>`
    );
  }

  return lines.join('\n    ');
}
